import { DimensionSource } from './DimensionSource';
import { DimensionSourceScale } from './DimensionSourceScale';
import { toCanvasTextAlign, toCanvasTextBaseline, PIXEL_RATIO } from './globalFunctions';
import { HorizontalAlignment } from './HorizontalAlignment';
import { Log } from './Log';
import { MultiLineFont } from './MultiLineFont';
import { Rect } from './Rect';
import { RenderDevice } from './RenderDevice';
import {
  ColorEffect,
  GlowEffect,
  ShadowEffect,
  TextRenderParameters,
} from './TextRenderParameters';
import { TextSprite } from './TextSprite';
import { Vector2 } from './Vector2';
import { VerticalAlignment } from './VerticalAlignment';

export class TtfFont extends MultiLineFont {
  private _renderDevice: RenderDevice;
  private _fontFamily: string;
  private _lines: string[];
  private _maximumTextWidth: number | null = null;
  private _context: CanvasRenderingContext2D;

  constructor(renderDevice: RenderDevice) {
    super();
    this._renderDevice = renderDevice;
  }

  private _initRenderingContextForText(
    canvas: HTMLCanvasElement,
    fontSize: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    dpr: number = 1
  ): CanvasRenderingContext2D {
    const context = canvas.getContext('2d')!;
    context.beginPath();
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (dpr !== 1) {
      context.scale(dpr, dpr);
    }
    context.textAlign = toCanvasTextAlign(halign);
    context.textBaseline = toCanvasTextBaseline(valign);
    context.fillStyle = '#fff';
    context.fontKerning = 'none';
    context.textRendering = 'geometricPrecision';

    context.font = this._font(fontSize);
    return context;
  }

  public build(
    parameters: TextRenderParameters,
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    multiline: boolean = true,
    cursorPosition: number = -1
  ): TextSprite {
    this._fontFamily = fontFamily;
    const dpr = parseFloat(PIXEL_RATIO?.toString() ?? '1.0');
    let canvas = document.createElement('canvas');

    canvas.width = size.x * dpr;
    canvas.height = size.y * dpr;
    let context = this._initRenderingContextForText(canvas, fontSize, halign, valign, dpr);
    let drawSize = size;

    if (text) {
      drawSize = this.drawText(
        context,
        parameters,
        text,
        size,
        fontSize,
        lineSpacing,
        multiline,
        cursorPosition
      );
    }

    if (drawSize.x !== size.x || drawSize.y !== size.y) {
      const newCanvas = document.createElement('canvas');
      newCanvas.width = drawSize.x * dpr;
      newCanvas.height = drawSize.y * dpr;
      context = this._initRenderingContextForText(newCanvas, fontSize, halign, valign, dpr);
      this.drawText(
        context,
        parameters,
        text,
        size,
        fontSize,
        lineSpacing,
        multiline,
        cursorPosition
      );
      canvas = newCanvas;
    }

    const texture = this._renderDevice.createTextureFromCanvas(canvas)!;
    this._context = context;
    const sprite = new TextSprite(
      texture,
      new Rect(Vector2.Zero, new Vector2(canvas.width, canvas.height)),
      new Rect(Vector2.Zero, drawSize)
    );
    return sprite;
  }

  private _font(fontSize: number): string {
    return `${fontSize.toString()}px '${this._fontFamily}'`;
  }

  private drawText(
    context: CanvasRenderingContext2D,
    parameters: TextRenderParameters,
    text: string,
    size: Vector2,
    fontSize: number,
    lineSpacing: number,
    multiline: boolean,
    cursorPosition: number
  ): Vector2 {
    const linesAndWidth = this._splitLinesAndGetMaxWidth(
      context,
      parameters.dimensionSourceScale,
      size,
      text,
      multiline,
      parameters.lang
    );
    const lines = linesAndWidth[0];
    const maxWidth = linesAndWidth[1];

    this._lines = lines;
    this._maximumTextWidth = maxWidth;
    fontSize = this._downscaleIfNeeded(context, parameters, size, fontSize, maxWidth);

    const x = this._getXPositionForText(context, size);
    const y = this._getYPositionForText(context, size, fontSize, lineSpacing, lines);

    this._drawLinesLayered(
      context,
      lines,
      fontSize,
      lineSpacing,
      x,
      y,
      cursorPosition,
      parameters.glowEffect,
      parameters.shadowEffect,
      parameters.colorEffect
    );

    let drawSize = size;
    const needIncreaseWidth =
      maxWidth > size.x && parameters.dimensionSourceScale !== DimensionSourceScale.DownOnly;
    if (needIncreaseWidth) {
      drawSize = new Vector2(needIncreaseWidth ? maxWidth : size.x, size.y);
    }

    return drawSize;
  }

  private _downscaleIfNeeded(
    context: CanvasRenderingContext2D,
    parameters: TextRenderParameters,
    size: Vector2,
    fontSize: number,
    maxWidth: number
  ): number {
    if (parameters.dimensionSourceScale === DimensionSourceScale.DownOnly && maxWidth > size.x) {
      fontSize = (fontSize * size.x) / maxWidth;
      context.font = this._font(fontSize);
    }
    return fontSize;
  }

  private _splitLinesAndGetMaxWidth(
    context: CanvasRenderingContext2D,
    dimensionSourceScale: DimensionSourceScale,
    size: Vector2,
    text: string,
    multiline: boolean,
    lang: Intl.LocalesArgument
  ): [string[], number] {
    let cutWidth = size.x;
    let maxWidth = 0.0;

    if (dimensionSourceScale === DimensionSourceScale.DownOnly) {
      cutWidth = 0.0;
    }

    const measureText = (text: string): number => context.measureText(text).width;

    let lines: string[];

    if (multiline) {
      lines = this.splitText(measureText, text, cutWidth, lang);

      maxWidth = lines
        .map((line) => context.measureText(line).width)
        .reduce((a, b) => Math.max(a, b), 0);
    } else {
      lines = [text];
      maxWidth = context.measureText(text).width;
    }

    return [lines, maxWidth];
  }

  private _getXPositionForText(context: CanvasRenderingContext2D, size: Vector2): number {
    let x = 0;

    switch (context.textAlign) {
      case 'center':
        x = size.x / 2;
        break;
      case 'right':
        x = size.x;
        break;
    }

    return x;
  }

  private _getYPositionForText(
    context: CanvasRenderingContext2D,
    size: Vector2,
    fontSize: number,
    lineSpacing: number,
    lines: string[],
  ): number {
    if (context.textBaseline === 'middle') {
      context.textBaseline = 'alphabetic';

      let maxAscent = 0;
      let maxDescent = 0;

      lines.forEach((line) => {
        //hack:
        let checkLine = line.trim();
        if(checkLine.length === 0) {
          checkLine = "1";
        }

        const metrics = context.measureText(checkLine);
        const ascent = metrics.actualBoundingBoxAscent;
        const descent = metrics.actualBoundingBoxDescent;


        maxAscent = Math.max(maxAscent, ascent);
        maxDescent = Math.max(maxDescent, descent);
      });

      const totalTextHeight = lines.length * fontSize + (lines.length - 1) * lineSpacing;

      const containerCenter = size.y * 0.5;
      return containerCenter - totalTextHeight * 0.5 + maxAscent;
    }

    if (context.textBaseline === 'bottom') {
      let maxAscent = 0;
      let maxDescent = 0;

      lines.forEach((line) => {
        const metrics = context.measureText(line);
        const ascent = metrics.actualBoundingBoxAscent;
        const descent = metrics.actualBoundingBoxDescent - metrics.ideographicBaseline;

        maxAscent = Math.max(maxAscent, ascent);
        maxDescent = Math.max(maxDescent, descent);
      });

      const totalTextHeight = lines.length * fontSize + (lines.length - 1) * lineSpacing;

      const lastLineBaseline = size.y - maxDescent;
      const firstLineY = lastLineBaseline - (totalTextHeight - fontSize);

      return firstLineY;
    }

    context.textBaseline = 'alphabetic';

    let maxAscent = 0;

    lines.forEach((line) => {
      const metrics = context.measureText(line);
      const ascent = metrics.actualBoundingBoxAscent;
      maxAscent = Math.max(maxAscent, ascent);
    });

    return maxAscent;
  }

  /**
   * Enhanced layered rendering method that renders effects in separate passes
   * similar to the HTML renderer approach
   */
  private _drawLinesLayered(
    context: CanvasRenderingContext2D,
    lines: string[],
    fontSize: number,
    lineSpacing: number,
    x: number,
    y: number,
    cursorPosition: number,
    glowEffect: GlowEffect | null,
    shadowEffect: ShadowEffect | null,
    colorEffect: ColorEffect | null
  ): void {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const currentY = y + i * (fontSize + lineSpacing);

      // Layer 1: Outer Glow (if glow effect exists)
      if (glowEffect) {
        this._renderOuterGlow(context, line, x, currentY, glowEffect);
      }

      // Layer 2: Shadow (if shadow effect exists)
      if (shadowEffect) {
        this._renderShadow(context, line, x, currentY, shadowEffect);
      }

      // Layer 3: Inner Glow (if glow effect exists)
      if (glowEffect) {
        this._renderInnerGlow(context, line, x, currentY, glowEffect);
      }

      // Layer 4: Main Text
      this._renderMainText(context, line, x, currentY, colorEffect);

      // Layer 5: Cursor (if needed)
      if (i === 0 && cursorPosition !== -1) {
        this._renderCursor(context, line, x, currentY, cursorPosition, fontSize);
      }
    }
  }

  /**
   * Renders outer glow effect with multiple layers for enhanced visual quality
   */
  private _renderOuterGlow(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    glowEffect: GlowEffect
  ): void {
    const glowColor = glowEffect.color;
    const baseBlur = Math.max(glowEffect.width, glowEffect.height);

    // Multiple glow layers for enhanced effect
    for (let layer = 0; layer < 8; layer++) {
      context.save();

      const intensity = 0.8 - layer * 0.1;
      const blur = baseBlur * (layer + 1) * 0.5;

      context.shadowColor = `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, ${intensity})`;
      context.shadowBlur = blur;
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;

      context.fillStyle = `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, ${intensity * 0.3})`;
      context.fillText(text, x, y);

      context.restore();
    }
  }

  /**
   * Renders shadow effect with enhanced blur and offset
   */
  private _renderShadow(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    shadowEffect: ShadowEffect
  ): void {
    context.save();

    const shadowColor = shadowEffect.color;
    const offsetX = shadowEffect.offsetX;
    const offsetY = shadowEffect.offsetY;
    const opacity = shadowEffect.opacity ?? 1.0;

    // Multiple shadow layers for better depth
    for (let layer = 0; layer < 3; layer++) {
      const intensity = (0.7 - layer * 0.2) * opacity;
      const blur = 6 + layer * 2;

      context.shadowColor = `rgba(${shadowColor.r}, ${shadowColor.g}, ${shadowColor.b}, ${intensity})`;
      context.shadowBlur = blur;
      context.shadowOffsetX = offsetX + layer;
      context.shadowOffsetY = offsetY + layer;

      context.fillStyle = `rgba(${shadowColor.r}, ${shadowColor.g}, ${shadowColor.b}, ${intensity * 0.5})`;
      context.fillText(text, x, y);
    }

    context.restore();
  }

  /**
   * Renders inner glow effect with multiple stroke layers
   */
  private _renderInnerGlow(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    glowEffect: GlowEffect
  ): void {
    const glowColor = glowEffect.color;
    const strokeWidth = Math.max(glowEffect.width, glowEffect.height);

    context.save();
    context.strokeStyle = glowColor.toCss();

    // Multiple stroke layers for enhanced inner glow
    for (let layer = 0; layer < 6; layer++) {
      const width = strokeWidth * (layer + 1) * 0.3;
      context.lineWidth = width;

      // Horizontal strokes
      context.strokeText(text, x - width, y);
      context.strokeText(text, x + width, y);

      // Vertical strokes
      context.strokeText(text, x, y - width);
      context.strokeText(text, x, y + width);

      // Diagonal strokes for more complete coverage
      context.strokeText(text, x - width, y - width);
      context.strokeText(text, x + width, y - width);
      context.strokeText(text, x - width, y + width);
      context.strokeText(text, x + width, y + width);
    }

    context.restore();
  }

  /**
   * Renders the main text with color effect
   */
  private _renderMainText(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    colorEffect: ColorEffect | null
  ): void {
    context.save();

    if (colorEffect) {
      context.fillStyle = colorEffect.color.toCss();
    } else {
      context.fillStyle = '#ffffff';
    }

    context.fillText(text, x, y);
    context.restore();
  }

  /**
   * Renders cursor for text input
   */
  private _renderCursor(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    cursorPosition: number,
    fontSize: number
  ): void {
    if (cursorPosition > text.length) {
      console.log('Probably bug');
      return;
    }

    const lineWidth = context.measureText(text).width;
    const cursorX =
      cursorPosition === 0 ? 0.0 : context.measureText(text.substring(0, cursorPosition)).width;

    const savedAlign = context.textAlign;
    let textStart = 0.0;

    switch (context.textAlign) {
      case 'center':
        textStart = x - lineWidth / 2;
        break;
      case 'right':
        textStart = x - lineWidth;
        break;
    }

    context.save();
    // context.textAlign = 'center';
    context.fillStyle = '#ffffff';
    context.fillText('|', textStart + cursorX, y - fontSize * 0.1);
    context.restore();

    context.textAlign = savedAlign;
  }

  /**
   * Legacy method for backward compatibility
   */
  private _drawLines(
    context: CanvasRenderingContext2D,
    lines: string[],
    fontSize: number,
    lineSpacing: number,
    x: number,
    y: number,
    cursorPosition: number,
    glowEffect: GlowEffect | null,
    shadowEffect: ShadowEffect | null,
    colorEffect: ColorEffect | null
  ): void {
    // Use the new layered approach
    this._drawLinesLayered(
      context,
      lines,
      fontSize,
      lineSpacing,
      x,
      y,
      cursorPosition,
      glowEffect,
      shadowEffect,
      colorEffect
    );
  }

  public get maximumTextWidth(): number | null {
    return this._maximumTextWidth;
  }

  public getLines(): string[] {
    return this._lines;
  }

  public measureText(text: string): number | null {
    if (!this._context) {
      return null;
    }
    return this._context.measureText(text).width;
  }

  public dispose(data: TextSprite): void {
    data.texture.dispose();
  }

  public calcCursorPositionForPoint(
    point: Vector2,
    parameters: TextRenderParameters,
    text: string,
    size: Vector2,
    fontFamily: string,
    fontSize: number,
    lineSpacing: number,
    halign: HorizontalAlignment,
    valign: VerticalAlignment,
    multiline: boolean = false
  ): [number, number] {
    const canvas = document.createElement('canvas');
    canvas.width = size.x;
    canvas.height = size.y;
    const context = canvas.getContext('2d')!;
    context.beginPath();
    context.clearRect(0, 0, size.x, size.y);
    context.textAlign = toCanvasTextAlign(halign);
    context.textBaseline = toCanvasTextBaseline(valign);
    context.fillStyle = '#fff';
    context.font = this._font(fontSize);

    if (text) {
      return this._calcCursorPositionForPointInner(
        point,
        context,
        parameters,
        text,
        size,
        fontSize,
        lineSpacing,
        multiline
      );
    }

    return [0, 0];
  }

  private _calcCursorPositionForPointInner(
    point: Vector2,
    context: CanvasRenderingContext2D,
    parameters: TextRenderParameters,
    text: string,
    size: Vector2,
    fontSize: number,
    lineSpacing: number,
    multiline: boolean
  ): [number, number] {
    const linesAndWidth = this._splitLinesAndGetMaxWidth(
      context,
      parameters.dimensionSourceScale,
      size,
      text,
      multiline,
      parameters.lang
    );
    const lines = linesAndWidth[0];
    const maxWidth = linesAndWidth[1];
    fontSize = this._downscaleIfNeeded(context, parameters, size, fontSize, maxWidth);

    const x = this._getXPositionForText(context, size);
    const y = this._getYPositionForText(context, size, fontSize, lineSpacing, lines);

    return this._calcCursorPositionForLines(point, context, lines, fontSize, lineSpacing, x, y);
  }

  private _calcCursorPositionForLines(
    point: Vector2,
    context: CanvasRenderingContext2D,
    lines: string[],
    fontSize: number,
    lineSpacing: number,
    x: number,
    y: number
  ): [number, number] {
    const binarySearch = (
      x: number,
      line: string,
      lineStart: number,
      startIndex: number,
      endIndex: number
    ): number => {
      if (x <= lineStart) {
        return startIndex;
      }
      if (line.length === 0) {
        return 0;
      }
      const widthToStart =
        startIndex === 0 ? 0.0 : context.measureText(line.substring(0, startIndex)).width;
      const widthToEnd = context.measureText(line.substring(0, endIndex + 1)).width;

      if (lineStart + widthToStart > x || lineStart + widthToEnd < x) {
        return -1;
      }

      if (endIndex <= startIndex) {
        if (lineStart + (widthToStart + widthToEnd) / 2 > x) {
          return startIndex;
        }
        return startIndex + 1;
      }

      const midIndex = Math.floor((startIndex + endIndex) / 2);
      const leftSearch = binarySearch(x, line, lineStart, startIndex, midIndex);

      if (leftSearch !== -1) {
        return leftSearch;
      }

      const rightSearch = binarySearch(x, line, lineStart, midIndex + 1, endIndex);
      return rightSearch;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      y += fontSize + lineSpacing;

      if (y >= point.y || i === line.length - 1) {
        if (line.length === 0) {
          return [0, i];
        }

        const textMetrics = context.measureText(line);
        let textStart = 0.0;

        switch (context.textAlign) {
          case 'center':
            textStart = x - textMetrics.width / 2;
            break;
          case 'right':
            textStart = x - textMetrics.width;
            break;
        }

        if (point.x <= textStart) {
          return [0, i];
        }

        if (point.x >= textStart + textMetrics.width) {
          return [line.length, i];
        }

        const cursorPosition = binarySearch(point.x, line, textStart, 0, line.length - 1);
        return [cursorPosition, i];
      }
    }

    return [0, 0];
  }

  public CalculateInverseScale(
    dimensionSource: DimensionSource,
    dimensionSourceScale: DimensionSourceScale,
    size: Vector2,
    bounds: Vector2
  ): Vector2 {
    let res: Vector2;

    switch (dimensionSource) {
      case DimensionSource.ProportionalSize: {
        const factorX = bounds.x / size.x;
        const factorY = bounds.y / size.y;
        let fx = factorX > factorY ? factorX : factorY;

        switch (dimensionSourceScale) {
          case DimensionSourceScale.DownOnly:
            if (fx < 1.0) {
              fx = 1.0;
            }
            break;
          case DimensionSourceScale.UpOnly:
            if (fx > 1.0) {
              fx = 1.0;
            }
            break;
        }

        res = new Vector2(fx, fx);
        break;
      }
      case DimensionSource.Size: {
        let factorX = bounds.x / size.x;
        let factorY = bounds.y / size.y;

        switch (dimensionSourceScale) {
          case DimensionSourceScale.DownOnly:
            if (factorX < 1.0) {
              factorX = 1.0;
            }
            if (factorY < 1.0) {
              factorY = 1.0;
            }
            break;
          case DimensionSourceScale.UpOnly:
            if (factorX > 1.0) {
              factorX = 1.0;
            }
            if (factorY > 1.0) {
              factorY = 1.0;
            }
            break;
        }

        res = new Vector2(factorX, factorY);
        break;
      }
      default:
        Log.Error(
          `CalculateInverseScale doesn't support DimensionSourceScale=${dimensionSourceScale}`
        );
        throw new Error(
          `CalculateInverseScale doesn't support DimensionSourceScale=${dimensionSourceScale}`
        );
    }

    return res;
  }
}
